// Fechas del Pulso (Pau 08-10-2026: «filtrar por fechas mucho más personalizado»). Puro: atajos, flechas ‹ › y
// rótulos. Todo en días UTC (el «día» del CRM y de la Sala). Los rangos son de días CERRADOS: hoy se ve aparte
// («Hoy», que va a medias y se compara a la misma hora).

/** Primer día con datos de Calypso en el CRM (espejo de backend/src/sala/historia/libro.ts). */
export const PRIMER_DIA = '2026-04-10';

export type Atajo = 'hoy' | 'ayer' | '7' | '30' | 'mes' | 'mes_pasado' | '90' | '180' | 'todo' | 'fechas';
export const ATAJOS: Atajo[] = ['hoy', 'ayer', '7', '30', 'mes', 'mes_pasado', '90', '180', 'todo'];

export interface Seleccion {
  atajo: Atajo;
  desde: string;
  hasta: string;
}

const DIA = 86_400_000;
export const isoDe = (ms: number) => new Date(ms).toISOString().slice(0, 10);
export const masDias = (d: string, n: number) => isoDe(Date.parse(`${d}T00:00:00Z`) + n * DIA);
export const diasEntre = (desde: string, hasta: string) => Math.round((Date.parse(`${hasta}T00:00:00Z`) - Date.parse(`${desde}T00:00:00Z`)) / DIA) + 1;
const primeroDeMes = (d: string) => `${d.slice(0, 7)}-01`;
const finDeMes = (d: string) => masDias(primeroDeMes(masDias(primeroDeMes(d), 32)), -1);
const max = (a: string, b: string) => (a > b ? a : b);

export function esMesNatural(desde: string, hasta: string): boolean {
  return desde === primeroDeMes(desde) && hasta === finDeMes(desde);
}

/** Las fechas de un atajo, a partir de hoy (día UTC). «Este mes» = del 1 a ayer (el 1 del mes, ayer). */
export function seleccionDe(atajo: Exclude<Atajo, 'fechas'>, hoy: string): Seleccion {
  const ayer = masDias(hoy, -1);
  const ultimos = (n: number): Seleccion => ({ atajo, desde: max(masDias(hoy, -n), PRIMER_DIA), hasta: ayer });
  switch (atajo) {
    case 'hoy':
      return { atajo, desde: hoy, hasta: hoy };
    case 'ayer':
      return { atajo, desde: ayer, hasta: ayer };
    case '7':
      return ultimos(7);
    case '30':
      return ultimos(30);
    case '90':
      return ultimos(90);
    case '180':
      return ultimos(180);
    case 'todo':
      return { atajo, desde: PRIMER_DIA, hasta: ayer };
    case 'mes':
      return { atajo, desde: hoy.endsWith('-01') ? primeroDeMes(ayer) : primeroDeMes(hoy), hasta: ayer };
    case 'mes_pasado': {
      const fin = masDias(primeroDeMes(hoy), -1);
      return { atajo, desde: primeroDeMes(fin), hasta: fin };
    }
  }
}

/** Una selección libre (del calendario), recortada a los días con datos y cerrados. Un solo día de hoy = «Hoy». */
export function seleccionLibre(a: string, b: string, hoy: string): Seleccion {
  let [desde, hasta] = a <= b ? [a, b] : [b, a];
  if (desde === hoy && hasta === hoy) return seleccionDe('hoy', hoy);
  const ayer = masDias(hoy, -1);
  if (hasta > ayer) hasta = ayer;
  if (desde < PRIMER_DIA) desde = PRIMER_DIA;
  if (desde > hasta) desde = hasta;
  return { atajo: desde === hasta && hasta === ayer ? 'ayer' : 'fechas', desde, hasta };
}

/** ‹ (−1) y › (+1): el periodo anterior o el siguiente de la misma forma (un día, un mes natural o N días). Null si
 * no hay a dónde ir (antes de los datos o después de hoy). */
export function desplazar(s: Seleccion, dir: -1 | 1, hoy: string): Seleccion | null {
  const ayer = masDias(hoy, -1);
  if (s.desde === s.hasta) {
    const d = masDias(s.desde, dir);
    if (d < PRIMER_DIA || d > hoy) return null;
    return d === hoy ? seleccionDe('hoy', hoy) : { atajo: d === ayer ? 'ayer' : 'fechas', desde: d, hasta: d };
  }
  // Un mes (natural o «este mes», que va del 1 a ayer): al mes anterior o al siguiente.
  if (s.desde === primeroDeMes(s.desde) && (s.hasta === finDeMes(s.desde) || (s.atajo === 'mes' && s.hasta === ayer))) {
    const desde = dir < 0 ? primeroDeMes(masDias(s.desde, -1)) : masDias(finDeMes(s.desde), 1);
    if (desde < primeroDeMes(PRIMER_DIA) || desde > ayer) return null;
    const hasta = finDeMes(desde) > ayer ? ayer : finDeMes(desde);
    return { atajo: hasta === ayer && desde === primeroDeMes(hoy) ? 'mes' : 'fechas', desde: max(desde, PRIMER_DIA), hasta };
  }
  const n = diasEntre(s.desde, s.hasta);
  const desde = masDias(s.desde, dir * n);
  const hasta = masDias(s.hasta, dir * n);
  if (hasta < PRIMER_DIA || desde > ayer) return null;
  return { atajo: 'fechas', desde: max(desde, PRIMER_DIA), hasta: hasta > ayer ? ayer : hasta };
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sept', 'oct', 'nov', 'dic'];
const MESES_LARGOS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const DIAS_SEMANA = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

const partes = (d: string) => ({ a: Number(d.slice(0, 4)), m: Number(d.slice(5, 7)) - 1, d: Number(d.slice(8, 10)) });

/** «8 oct» */
export function diaCorto(d: string): string {
  const p = partes(d);
  return `${p.d} ${MESES[p.m]}`;
}

/** «jue 8 oct» */
export function diaConSemana(d: string): string {
  return `${DIAS_SEMANA[new Date(`${d}T00:00:00Z`).getUTCDay()]} ${diaCorto(d)}`;
}

export function mesLargo(d: string): string {
  const p = partes(d);
  return `${MESES_LARGOS[p.m]![0]!.toUpperCase()}${MESES_LARGOS[p.m]!.slice(1)} ${p.a}`;
}

/** Rótulo del periodo: «jue 8 oct», «Septiembre 2026», «1–7 oct», «10 jul – 7 oct». */
export function rotulo(desde: string, hasta: string): string {
  if (desde === hasta) return diaConSemana(desde);
  if (esMesNatural(desde, hasta)) return mesLargo(desde);
  const a = partes(desde);
  const b = partes(hasta);
  if (a.m === b.m && a.a === b.a) return `${a.d}–${b.d} ${MESES[b.m]}`;
  return `${diaCorto(desde)} – ${diaCorto(hasta)}${a.a !== b.a ? ` ${b.a}` : ''}`;
}

/** Semanas (lunes a domingo) de un mes, con null en los huecos: para el calendario. */
export function semanasDelMes(mes: string): Array<Array<string | null>> {
  const inicio = primeroDeMes(mes);
  const fin = finDeMes(mes);
  const hueco = (new Date(`${inicio}T00:00:00Z`).getUTCDay() + 6) % 7;
  const celdas: Array<string | null> = Array<string | null>(hueco).fill(null);
  for (let d = inicio; d <= fin; d = masDias(d, 1)) celdas.push(d);
  while (celdas.length % 7) celdas.push(null);
  const semanas: Array<Array<string | null>> = [];
  for (let i = 0; i < celdas.length; i += 7) semanas.push(celdas.slice(i, i + 7));
  return semanas;
}

/** Meses desde el primero con datos hasta el de hoy («Ir a…»). */
export function mesesConDatos(hoy: string): string[] {
  const res: string[] = [];
  for (let m = primeroDeMes(PRIMER_DIA); m <= hoy; m = masDias(finDeMes(m), 1)) res.push(m);
  return res;
}

export const mesCorto = (m: string) => MESES[partes(m).m]!;
export { finDeMes, primeroDeMes };
